/**
 * ProjectCard component for displaying project summary information
 * Shows project name, description, repository, stats, and actions
 */
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FolderKanban,
  GitBranch,
  Archive,
  Pencil,
  Trash2,
  MessageSquare,
  Activity
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ProjectSummary } from '@/lib/types'

export interface ProjectCardProps {
  /** Project data to display */
  project: ProjectSummary
  /** Callback when edit button is clicked */
  onEdit?: (projectId: string) => void
  /** Callback when delete button is clicked */
  onDelete?: (projectId: string) => void
  /** Card variant */
  variant?: 'default' | 'compact'
  /** Additional CSS classes */
  className?: string
}

/**
 * Truncate description text
 */
function truncateDescription(text: string | undefined, maxLength = 120): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

/**
 * Extract repo name from URL
 */
function extractRepoName(url: string): string {
  try {
    // Handle GitHub URLs like https://github.com/org/repo
    const match = url.match(/github\.com\/([^/]+\/[^/]+)/i)
    if (match) return match[1]

    // Handle Azure DevOps URLs
    const azureMatch = url.match(/dev\.azure\.com\/[^/]+\/[^/]+\/_git\/([^/]+)/i)
    if (azureMatch) return azureMatch[1]

    // Fallback: return last path segment
    const parts = url.split('/').filter(Boolean)
    return parts[parts.length - 1] || url
  } catch {
    return url
  }
}

/**
 * Card component for displaying a project summary.
 * Shows project name, description, repository info, session counts, and action buttons.
 *
 * @example
 * ```tsx
 * <ProjectCard
 *   project={project}
 *   onEdit={(id) => navigate(`/projects/${id}/edit`)}
 *   onDelete={(id) => deleteProject(id)}
 * />
 * ```
 */
export function ProjectCard({
  project,
  onEdit,
  onDelete,
  variant = 'default',
  className
}: ProjectCardProps) {
  const {
    id,
    name,
    description,
    defaultRepository,
    isArchived,
    sessionCount = 0,
    activeSessionCount = 0
  } = project

  const hasActions = onEdit || onDelete
  const isCompact = variant === 'compact'

  return (
    <Card
      className={cn(
        'relative transition-all hover:bg-accent/50 hover:shadow-md h-[220px] flex flex-col rounded-xl',
        isArchived && 'opacity-70',
        className
      )}
    >
      <Link
        to={`/projects/${id}`}
        className="absolute inset-0 z-0"
        aria-label={isArchived ? `${name} (archived)` : name}
      />

      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FolderKanban className="h-4 w-4 text-primary" />
            </div>
            <h3 className="font-semibold text-base truncate">{name}</h3>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isArchived && (
              <Badge
                variant="secondary"
                className="gap-1"
                aria-label="Project is archived"
              >
                <Archive className="h-3 w-3" />
                Archived
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex-1 flex flex-col">
        {/* Description (hide in compact mode) */}
        {!isCompact && (
          <p
            className="text-sm text-muted-foreground mb-3 line-clamp-2 min-h-[2.5rem]"
            data-testid="project-description"
          >
            {description ? truncateDescription(description, 100) : '\u00A0'}
          </p>
        )}

        {/* Repository info */}
        {defaultRepository && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <GitBranch className="h-4 w-4 shrink-0" />
            <span className="truncate">{extractRepoName(defaultRepository)}</span>
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-auto">
          <div className="flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" />
            <span>{sessionCount} sessions</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Activity className="h-4 w-4" />
            <span>{activeSessionCount} active</span>
          </div>
        </div>

        {/* Actions */}
        {hasActions && (
          <div className="flex items-center gap-2 mt-3 relative z-10">
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onEdit(id)
                }}
                aria-label={`Edit ${name}`}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onDelete(id)
                }}
                aria-label={`Delete ${name}`}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
